import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  BuildingOffice2Icon,
  PlusIcon,
  UsersIcon,
  PencilIcon,
  ArrowRightCircleIcon,
  ClipboardDocumentIcon,
  CheckCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { companyService, setWorkspace } from '../../services/api';
import { Spinner } from '../../components/Spinner';

// ── Add / Edit workspace modal ───────────────────────────────────────────────
function WorkspaceModal({ mode, workspace, onClose, onSaved }) {
  const isEdit = mode === 'edit';
  const [form, setForm] = useState(
    isEdit
      ? { name: workspace.name, maxUsers: workspace.maxUsers, status: workspace.status }
      : { workspaceName: '', firstName: '', lastName: '', email: '', password: '', maxUsers: 5 }
  );
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      if (isEdit) {
        await companyService.updateWorkspace(workspace._id, {
          name: form.name, maxUsers: Number(form.maxUsers), status: form.status,
        });
        toast.success('Workspace updated');
      } else {
        const res = await companyService.createWorkspace({ ...form, maxUsers: Number(form.maxUsers) });
        const sa = res.data?.data?.superAdmin;
        toast.success(`Super admin created${sa?.userCode ? ` (${sa.userCode})` : ''}`);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">
            {isEdit ? `Edit Workspace — ${workspace.name}` : 'Add Super Admin / Workspace'}
          </h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><XMarkIcon className="h-5 w-5" /></button>
        </div>

        <form onSubmit={submit} className="px-5 py-5 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">{error}</div>}

          {isEdit ? (
            <>
              <Field label="Workspace Name">
                <input className="input" value={form.name} onChange={e => set('name', e.target.value)} required />
              </Field>
              <Field label="Status">
                <select className="input" value={form.status} onChange={e => set('status', e.target.value)}>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                </select>
              </Field>
            </>
          ) : (
            <>
              <Field label="Workspace Name">
                <input className="input" value={form.workspaceName} onChange={e => set('workspaceName', e.target.value)} placeholder="e.g. North Zone" required />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="First Name">
                  <input className="input" value={form.firstName} onChange={e => set('firstName', e.target.value)} required />
                </Field>
                <Field label="Last Name">
                  <input className="input" value={form.lastName} onChange={e => set('lastName', e.target.value)} required />
                </Field>
              </div>
              <Field label="Super Admin Email">
                <input type="email" className="input" value={form.email} onChange={e => set('email', e.target.value)} required />
              </Field>
              <Field label="Password">
                <input type="text" className="input" value={form.password} onChange={e => set('password', e.target.value)} placeholder="min 6 characters" required />
              </Field>
            </>
          )}

          <Field label="User Limit (includes the super admin)">
            <input type="number" min="1" className="input" value={form.maxUsers} onChange={e => set('maxUsers', e.target.value)} required />
          </Field>

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            <button type="button" onClick={onClose} className="btn-outline-secondary" disabled={submitting}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? <><Spinner size="sm" className="mr-2" />Saving...</> : (isEdit ? 'Save Changes' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    {children}
  </div>
);

// ── Workspaces page ──────────────────────────────────────────────────────────
export const CompanyConsole = () => {
  const queryClient = useQueryClient();
  const [modal, setModal] = useState(null);

  const { data, isLoading } = useQuery('companyWorkspaces', companyService.getWorkspaces);
  const workspaces = data?.data?.data?.workspaces || [];

  const refresh = () => queryClient.invalidateQueries('companyWorkspaces');

  const openWorkspace = (ws) => {
    if (ws.status !== 'active') { toast.error('This workspace is suspended'); return; }
    setWorkspace(ws._id, ws.name);
    window.location.assign('/dashboard'); // full reload so all queries use the new workspace
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Workspaces</h1>
          <p className="mt-1 text-gray-600">Each workspace is an isolated super admin and their team.</p>
        </div>
        <button onClick={() => setModal({ mode: 'add' })} className="btn-primary">
          <PlusIcon className="h-4 w-4 mr-2" /> Add Super Admin
        </button>
      </div>

      {isLoading ? (
        <div className="p-12 text-center"><Spinner size="lg" /></div>
      ) : workspaces.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
          <BuildingOffice2Icon className="mx-auto h-12 w-12 text-gray-300" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No workspaces yet</h3>
          <p className="mt-1 text-sm text-gray-500">Create your first super admin to get started.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {workspaces.map(ws => {
            const sa = ws.superAdmins?.[0];
            const full = ws.userCount >= ws.maxUsers;
            return (
              <div key={ws._id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 truncate">{ws.name}</h3>
                    <p className="text-xs text-gray-500 truncate mt-0.5">
                      {sa ? `${sa.firstName} ${sa.lastName} · ${sa.email}` : 'No super admin'}
                    </p>
                  </div>
                  <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                    ws.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                  }`}>{ws.status}</span>
                </div>

                <div className="mt-4 flex items-center gap-2 text-sm">
                  <UsersIcon className="h-4 w-4 text-gray-400" />
                  <span className={full ? 'text-red-600 font-medium' : 'text-gray-700'}>
                    {ws.userCount} / {ws.maxUsers} users
                  </span>
                </div>

                <div className="mt-5 pt-4 border-t border-gray-100 flex items-center gap-2">
                  <button onClick={() => openWorkspace(ws)} className="btn-primary flex-1 justify-center">
                    <ArrowRightCircleIcon className="h-4 w-4 mr-1.5" /> Open
                  </button>
                  <button onClick={() => setModal({ mode: 'edit', workspace: ws })} className="btn-outline-secondary">
                    <PencilIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <WorkspaceModal mode={modal.mode} workspace={modal.workspace} onClose={() => setModal(null)} onSaved={refresh} />
      )}
    </div>
  );
};

// ── Facebook App page (shared Meta app) ──────────────────────────────────────
const ReadOnlyRow = ({ label, value, copied, onCopy, k }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
    <div className="flex items-center bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
      <span className="flex-1 text-sm font-mono text-gray-700 break-all">{value || '—'}</span>
      {value && (
        <button onClick={() => onCopy(k, value)} className="ml-2 p-1 text-gray-400 hover:text-primary-600">
          {copied === k ? <CheckCircleIcon className="h-4 w-4 text-green-500" /> : <ClipboardDocumentIcon className="h-4 w-4" />}
        </button>
      )}
    </div>
  </div>
);

export const CompanyFacebookApp = () => {
  const { data, refetch } = useQuery('companyMetaApp', companyService.getMetaApp);
  const meta = data?.data?.data || {};
  const [appId, setAppId] = useState('');
  const [secret, setSecret] = useState('');
  const [configId, setConfigId] = useState('');
  const [copied, setCopied] = useState('');
  const [saving, setSaving] = useState(false);

  React.useEffect(() => { if (meta.metaAppId !== undefined) setAppId(meta.metaAppId || ''); }, [meta.metaAppId]);
  React.useEffect(() => { if (meta.metaLoginConfigId !== undefined) setConfigId(meta.metaLoginConfigId || ''); }, [meta.metaLoginConfigId]);

  const copy = (label, val) => navigator.clipboard.writeText(val).then(() => { setCopied(label); setTimeout(() => setCopied(''), 1500); });

  const save = async () => {
    setSaving(true);
    try {
      await companyService.saveMetaApp({ metaAppId: appId, metaAppSecret: secret, metaLoginConfigId: configId });
      toast.success('Meta app settings saved');
      setSecret('');
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Facebook App</h1>
      <p className="text-gray-600 mb-6">One shared Meta app, reviewed once, used by every workspace. Each super admin connects their own pages.</p>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 max-w-3xl">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Meta App ID">
            <input className="input" value={appId} onChange={e => setAppId(e.target.value)} placeholder="e.g. 123456789012345" />
          </Field>
          <Field label={`Meta App Secret${meta.hasSecret ? ' (set — leave blank to keep)' : ''}`}>
            <input type="password" className="input" value={secret} onChange={e => setSecret(e.target.value)} placeholder={meta.hasSecret ? '••••••••' : 'enter secret'} />
          </Field>
          <Field label="Facebook Login Configuration ID">
            <input className="input" value={configId} onChange={e => setConfigId(e.target.value)} placeholder="e.g. 1687831775637645" />
          </Field>
        </div>

        <div className="mt-4 space-y-2">
          <ReadOnlyRow label="Webhook Callback URL" value={meta.webhookUrl || ''} copied={copied} onCopy={copy} k="url" />
          <ReadOnlyRow label="Verify Token" value={meta.metaWebhookVerifyToken || ''} copied={copied} onCopy={copy} k="token" />
        </div>

        <div className="mt-4 flex justify-end">
          <button onClick={save} className="btn-primary" disabled={saving}>
            {saving ? <><Spinner size="sm" className="mr-2" />Saving...</> : 'Save Meta Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};
